import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql-client";
import { CompanyTierQuery } from "../queries";
import { ResultOf } from "gql.tada";
import { useRouter } from "next/router";
import { CompanyPlanQuery, CompanyPlan } from "@/hooks/company-plan-query";
import { AUTH_DISABLED_PAGES } from "@/constants/routes";

export type CompanyTier = NonNullable<
  ResultOf<typeof CompanyTierQuery>["company"]["subscription"]
>["tier"];

type CompanySubscriptionNonNull = NonNullable<CompanyPlan["subscription"]>;

export type CompanyTierContextPlan = {
  sku: CompanySubscriptionNonNull["planPriceId"];
  name: CompanySubscriptionNonNull["tier"]["name"];
  billingInterval: CompanySubscriptionNonNull["plan"]["billingInterval"];
  tier: CompanySubscriptionNonNull["tier"];
};

type CompanyTierContextType = {
  plan: CompanyTierContextPlan | null;
  isLoading: boolean;
};

const CompanyTierContext = createContext<CompanyTierContextType | null>(null);

type Props = {
  children: ReactNode;
};

export function CompanyTierProvider({ children }: Props) {
  const router = useRouter();

  // only enable the query if we're on the client and router is ready
  // this ensures we don't make requests during SSR or before authentication is established
  const isClient = typeof window !== "undefined";

  // check if page is auth-disabled (e.g. login, signup, version), skip company query there
  const shouldFetch =
    isClient &&
    router.isReady &&
    !(AUTH_DISABLED_PAGES as readonly string[]).includes(router.pathname);

  const { data: subscription, isLoading } = useQuery({
    queryKey: ["companyPlan"],
    queryFn: () =>
      graphqlClient
        .request(CompanyPlanQuery)
        .then((res) => res.company.subscription),
    enabled: shouldFetch,
    staleTime: 1 * 60 * 1000, // consider data fresh for 1 minute
    gcTime: 2 * 60 * 1000, // keep in cache for 2 minutes after last access
  });

  const contextValue: CompanyTierContextType = {
    plan: subscription?.plan
      ? {
          sku: subscription.planPriceId,
          name: subscription.tier.name,
          billingInterval: subscription.plan.billingInterval,
          tier: subscription.tier,
        }
      : null,
    isLoading,
  };

  return (
    <CompanyTierContext.Provider value={contextValue}>
      {children}
    </CompanyTierContext.Provider>
  );
}

export function useCompanyTierContext() {
  const context = useContext(CompanyTierContext);
  if (!context) {
    throw new Error(
      "useCompanyTierContext must be used within a CompanyTierProvider",
    );
  }
  return context;
}
